import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { broadcastEntityChange } from "@/lib/useEntityRefresh";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound, ExternalLink, Copy, Pencil, Users } from "lucide-react";
import { toast } from "sonner";

export default function SubClientes() {
  const [subClientes, setSubClientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [espacios, setEspacios] = useState([]);
  const [cadenas, setCadenas] = useState([]);
  const [editing, setEditing] = useState(null);  // null o sub-cliente
  const [openForm, setOpenForm] = useState(false);
  const [usersDialog, setUsersDialog] = useState(null);  // sub-cliente para gestionar usuarios

  const load = async () => {
    try {
      const [sc, cli, esp, cad] = await Promise.all([
        api.get("/sub-clientes"),
        api.get("/clientes"),
        api.get("/espacios"),
        api.get("/cadenas"),
      ]);
      setSubClientes(sc.data || []);
      setClientes(cli.data || []);
      setEspacios(esp.data || []);
      setCadenas(cad.data || []);
    } catch { toast.error("Error cargando datos"); }
  };
  useEffect(() => { load(); }, []);

  const del = async (sid) => {
    if (!window.confirm("¿Eliminar este sub-cliente y todos sus usuarios?")) return;
    try {
      await api.delete(`/sub-clientes/${sid}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6" data-testid="sub-clientes-page">
      <PageHeader
        title="Sub-clientes (portal white-label)"
        subtitle='"El cliente de mi cliente" — Decor Galva crea Lancôme, le da acceso de sólo-lectura'
        action={
          <Button onClick={() => { setEditing(null); setOpenForm(true); }}
            data-testid="btn-nuevo-sub" className="bg-violet-500 text-zinc-950 hover:bg-violet-400">
            <Plus className="w-4 h-4 mr-1" /> Nuevo sub-cliente
          </Button>
        }
      />

      <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg overflow-hidden">
        {subClientes.length === 0 ? (
          <div className="text-center py-10 text-zinc-500" data-testid="sub-vacio">
            Aún no has creado ningún sub-cliente. Pulsa "Nuevo sub-cliente".
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2.5">Marca</th>
                <th className="text-left p-2.5">Cliente raíz</th>
                <th className="text-left p-2.5">Slug</th>
                <th className="text-right p-2.5">Espacios</th>
                <th className="text-right p-2.5">Cadenas</th>
                <th className="text-right p-2.5">Usuarios</th>
                <th className="text-right p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {subClientes.map((s) => {
                const cli = clientes.find((c) => c.id === s.cliente_dnf_id);
                const portalUrl = `${window.location.origin}/portal-sub/login?slug=${s.slug}`;
                return (
                  <tr key={s.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`sub-row-${s.slug}`}>
                    <td className="p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded shrink-0" style={{ background: s.color_primario || "#f59e0b" }} />
                        <div>
                          <div className="font-medium text-zinc-100">{s.nombre}</div>
                          {!s.activo && <div className="text-[10px] text-red-400">desactivado</div>}
                        </div>
                      </div>
                    </td>
                    <td className="p-2.5 text-zinc-300">{cli?.nombre || "—"}</td>
                    <td className="p-2.5 font-mono text-xs text-zinc-400">{s.slug}</td>
                    <td className="p-2.5 text-right font-mono text-zinc-300">{s.espacios_ids?.length || 0}</td>
                    <td className="p-2.5 text-right font-mono text-zinc-300">{s.cadenas_ids?.length || 0}</td>
                    <td className="p-2.5 text-right font-mono">
                      <button onClick={() => setUsersDialog(s)}
                        className="text-violet-300 hover:text-violet-200 inline-flex items-center gap-1"
                        data-testid={`btn-users-${s.slug}`}>
                        <Users className="w-3 h-3" /> {s._n_usuarios || 0}
                      </button>
                    </td>
                    <td className="p-2.5 text-right whitespace-nowrap">
                      <button onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("URL copiada"); }}
                        title="Copiar URL portal"
                        className="text-zinc-400 hover:text-amber-300 mr-1 p-1"
                        data-testid={`btn-copy-${s.slug}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <a href={portalUrl} target="_blank" rel="noopener noreferrer"
                        title="Abrir portal"
                        className="text-zinc-400 hover:text-amber-300 mr-1 p-1 inline-block">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <button onClick={() => { setEditing(s); setOpenForm(true); }}
                        className="text-zinc-400 hover:text-amber-300 mr-1 p-1"
                        data-testid={`btn-edit-${s.slug}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => del(s.id)}
                        className="text-zinc-400 hover:text-red-300 p-1"
                        data-testid={`btn-del-${s.slug}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <SubClienteForm open={openForm} onClose={() => setOpenForm(false)}
        editing={editing} clientes={clientes} espacios={espacios} cadenas={cadenas}
        onSaved={load} />
      <UsersDialog sub={usersDialog} onClose={() => setUsersDialog(null)} />
    </div>
  );
}


function SubClienteForm({ open, onClose, editing, clientes, espacios, cadenas, onSaved }) {
  const [form, setForm] = useState({
    cliente_dnf_id: "", slug: "", nombre: "", color_primario: "#f59e0b", logo_url: "",
    espacios_ids: [], cadenas_ids: [], activo: true,
  });
  useEffect(() => {
    if (editing) {
      setForm({
        cliente_dnf_id: editing.cliente_dnf_id,
        slug: editing.slug,
        nombre: editing.nombre,
        color_primario: editing.color_primario || "#f59e0b",
        logo_url: editing.logo_url || "",
        espacios_ids: editing.espacios_ids || [],
        cadenas_ids: editing.cadenas_ids || [],
        activo: editing.activo,
      });
    } else {
      setForm({
        cliente_dnf_id: "", slug: "", nombre: "", color_primario: "#f59e0b", logo_url: "",
        espacios_ids: [], cadenas_ids: [], activo: true,
      });
    }
  }, [editing, open]);

  // Filtrar espacios y cadenas por cliente DNF seleccionado
  const espaciosCliente = espacios.filter((e) => !form.cliente_dnf_id || e.cliente_id === form.cliente_dnf_id);
  const cadenasCliente = cadenas.filter((c) => !form.cliente_dnf_id || c.cliente_id === form.cliente_dnf_id);

  const toggleId = (arr, id) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const submit = async () => {
    if (!editing && (!form.cliente_dnf_id || !form.slug || !form.nombre)) {
      return toast.error("Cliente raíz, slug y nombre son obligatorios");
    }
    try {
      if (editing) {
        const { cliente_dnf_id, slug, ...patch } = form;
        await api.patch(`/sub-clientes/${editing.id}`, patch);
        broadcastEntityChange("subcliente", { action: "update" });
        toast.success("Actualizado");
      } else {
        await api.post("/sub-clientes", form);
        toast.success("Creado");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-sub-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar sub-cliente" : "Nuevo sub-cliente"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Marca el cliente raíz DNF al que pertenece (ej. Decor Galva) y a qué espacios o cadenas tendrá acceso este sub-cliente (ej. Lancôme).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Cliente raíz DNF *</Label>
            <Select value={form.cliente_dnf_id}
              onValueChange={(v) => setForm({ ...form, cliente_dnf_id: v })}
              disabled={!!editing}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 h-9 text-sm" data-testid="form-cliente-raiz">
                <SelectValue placeholder="Selecciona cliente raíz" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Slug (URL) *</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="lancome" disabled={!!editing}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm h-9" data-testid="form-slug" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Nombre visible *</Label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Lancôme España" className="bg-zinc-900 border-zinc-800 h-9" data-testid="form-nombre" />
          </div>
          <div>
            <Label className="text-xs">Logo URL (opcional)</Label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://…" className="bg-zinc-900 border-zinc-800 h-9 text-xs" data-testid="form-logo" />
          </div>
          <div>
            <Label className="text-xs">Color primario</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.color_primario}
                onChange={(e) => setForm({ ...form, color_primario: e.target.value })}
                className="w-12 h-9 p-1 bg-zinc-900 border-zinc-800" data-testid="form-color" />
              <Input value={form.color_primario}
                onChange={(e) => setForm({ ...form, color_primario: e.target.value })}
                className="bg-zinc-900 border-zinc-800 h-9 font-mono text-xs flex-1" />
            </div>
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Espacios accesibles ({form.espacios_ids.length})</Label>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 max-h-44 overflow-y-auto space-y-1">
                {espaciosCliente.length === 0
                  ? <div className="text-xs text-zinc-500">Sin espacios para este cliente</div>
                  : espaciosCliente.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-zinc-800/60 px-1.5 py-1 rounded">
                      <input type="checkbox" checked={form.espacios_ids.includes(e.id)}
                        onChange={() => setForm({ ...form, espacios_ids: toggleId(form.espacios_ids, e.id) })} />
                      <span className="text-zinc-300">{e.nombre}</span>
                      <span className="text-[10px] text-zinc-500">· {e.ciudad}</span>
                    </label>
                  ))}
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Cadenas completas ({form.cadenas_ids.length})</Label>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 max-h-44 overflow-y-auto space-y-1">
                {cadenasCliente.length === 0
                  ? <div className="text-xs text-zinc-500">Sin cadenas para este cliente</div>
                  : cadenasCliente.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-zinc-800/60 px-1.5 py-1 rounded">
                      <input type="checkbox" checked={form.cadenas_ids.includes(c.id)}
                        onChange={() => setForm({ ...form, cadenas_ids: toggleId(form.cadenas_ids, c.id) })} />
                      <span className="text-zinc-300">{c.nombre}</span>
                    </label>
                  ))}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">Al elegir cadena, verán <b>todos</b> los espacios de esa cadena.</div>
            </div>
          </div>

          {editing && (
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.activo}
                onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                data-testid="form-activo" />
              <Label className="text-xs">Activo (desactivar bloquea el acceso al portal)</Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} className="bg-violet-500 text-zinc-950 hover:bg-violet-400" data-testid="form-guardar">
            {editing ? "Guardar cambios" : "Crear sub-cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function UsersDialog({ sub, onClose }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", nombre: "" });
  const [generated, setGenerated] = useState(null);

  const load = async () => {
    if (!sub) return;
    try {
      const { data } = await api.get(`/sub-clientes/${sub.id}/usuarios`);
      setUsers(data || []);
    } catch { toast.error("Error usuarios"); }
  };
  useEffect(() => { load(); setGenerated(null); setForm({ email: "", nombre: "" }); }, [sub?.id]);

  const crear = async () => {
    if (!form.email || !form.nombre) return toast.error("Email y nombre obligatorios");
    try {
      const { data } = await api.post(`/sub-clientes/${sub.id}/usuarios`, form);
      setGenerated({ email: data.user.email, password: data.password_generada });
      setForm({ email: "", nombre: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const del = async (uid) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    await api.delete(`/sub-clientes/usuarios/${uid}`);
    load();
  };

  return (
    <Dialog open={!!sub} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-users-sub">
        <DialogHeader>
          <DialogTitle>Usuarios de {sub?.nombre}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Personas que pueden entrar al portal sub-cliente con email + contraseña.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {generated && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs"
              data-testid="cred-gen">
              <div className="text-emerald-300 mb-1">✓ Usuario creado. Copia estas credenciales:</div>
              <div className="font-mono text-zinc-100">{generated.email}</div>
              <div className="font-mono text-zinc-100">{generated.password}
                <button onClick={() => { navigator.clipboard.writeText(generated.password); toast.success("Copiado"); }}
                  className="ml-2 text-amber-300 hover:text-amber-200" title="Copiar">
                  <Copy className="w-3 h-3 inline" />
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-zinc-900 border-zinc-800 h-9 text-sm" data-testid="form-user-email" />
            <Input placeholder="Nombre" value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="bg-zinc-900 border-zinc-800 h-9 text-sm" data-testid="form-user-nombre" />
          </div>
          <Button onClick={crear} className="w-full bg-emerald-500 text-zinc-950 hover:bg-emerald-400" data-testid="btn-crear-user">
            <KeyRound className="w-4 h-4 mr-1.5" /> Crear usuario (password auto)
          </Button>

          <div className="border-t border-zinc-800 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Usuarios actuales</div>
            {users.length === 0 ? (
              <div className="text-xs text-zinc-500">Sin usuarios todavía</div>
            ) : users.map((u) => (
              <div key={u.id} className="flex items-center gap-2 py-1.5 text-xs">
                <div className="flex-1">
                  <div className="text-zinc-100">{u.nombre}</div>
                  <div className="text-zinc-500 font-mono">{u.email}</div>
                </div>
                {u.last_login && <span className="text-[10px] text-zinc-500">last login {u.last_login.slice(0, 10)}</span>}
                <button onClick={() => del(u.id)} className="text-zinc-400 hover:text-red-300 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
